import { Component, ReactNode } from 'react'
import * as Sentry from '@sentry/react'

interface Props { children: ReactNode }
interface State { error: Error | null }

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  // getDerivedStateFromError is render-only (React's own convention) — error
  // reporting as a side effect belongs in componentDidCatch. Without this,
  // every error this boundary catches was previously invisible to any
  // monitoring at all, not just Sentry.
  componentDidCatch(error: Error, errorInfo: { componentStack: string }) {
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } })
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center p-8 bg-orange-50 dark:bg-zinc-900">
          <div className="max-w-md w-full border-4 border-black dark:border-white bg-white dark:bg-zinc-800 p-8 shadow-[6px_6px_0_black] dark:shadow-[6px_6px_0_white]">
            <h1 className="text-2xl font-black mb-2">Something broke 🎲</h1>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-6">
              An unexpected error occurred. Reload the page to continue — your decisions are saved.
            </p>
            <pre className="text-xs bg-zinc-100 dark:bg-zinc-900 p-3 rounded overflow-auto mb-6 max-h-40 whitespace-pre-wrap break-words">
              {this.state.error.message}
            </pre>
            <button
              onClick={() => window.location.reload()}
              className="bg-orange-400 border-2 border-black font-bold px-4 py-2 shadow-[3px_3px_0_black] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
            >
              Reload page
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
